import styled from 'styled-components'

import { Typography } from 'components/Typography/Typography'
import Checkbox from 'components/Checkbox/Checkbox'
import { Button } from 'components/Button/Button'
import { IconLink } from 'components/Icons/IconLink'
import { Stylable } from 'utils/types'
import { useAppTheme } from 'contexts/ThemeProviderContext'

const Container = styled.div`
  margin: 16px 0;
  padding: 24px;
  border-radius: 16px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};
`

const Divider = styled.div`
  height: 1px;
  align-self: stretch;
  background: ${p => p.theme.colors.text.primary};
  margin: 16px 0;
`

const CardContainer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: space-between;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
  }
`

const Img = styled.img`
  width: 368px;
  height: 200px;
  border-radius: 12px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 250px;
    height: 137px;
  }
`

const CardItem = styled.div`
  text-align: center;
  & > div {
    margin: auto;
  }

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    width: calc(33.33% - 16px);
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-bottom: 16px;
  }
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    & > div {
      font-size: ${p => p.theme.typography.size.Body}px;
    }
    gap: 8px;
    flex-direction: column;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: 16px;
  }
`

const SeeDocumentation = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  & > a {
    text-decoration: none;
    color: ${p => p.theme.colors.text.primaryDarkBackground};
    display: flex;
    align-items: center;
    & > svg {
      margin-left: 8px;
    }
  }
`

interface Props extends Stylable {
  onDontShowThisAgainClick: () => void
  isDontShowSelected: boolean
}

export const SingleVaultExplanation = ({ onDontShowThisAgainClick, isDontShowSelected, style }: Props) => {
  const { theme } = useAppTheme()

  if (isDontShowSelected) {
    return null
  }

  return (
    <Container style={style}>
      <TitleContainer>
        <Typography variant="label1" fontWeight="bold">
          Single Asset Vaults
        </Typography>
        <Checkbox label="Don’t show this again." checked={isDontShowSelected} onChange={onDontShowThisAgainClick} />
      </TitleContainer>
      <Divider />
      <CardContainer>
        <CardItem>
          <Img src={theme === 'dark' ? '/single-asset-vault1-dark.png' : '/single-asset-vault1.png'} />
          <Typography variant="label2" fontWeight="light" color="secondary" style={{ marginTop: 16 }}>
            Earn passive income with idle tokens using automated, auto-compounding yield strategies.
          </Typography>
        </CardItem>
        <CardItem>
          <Img src={theme === 'dark' ? '/single-asset-vault2-dark.png' : '/single-asset-vault2.png'} />
          <Typography variant="label2" fontWeight="light" color="secondary" style={{ marginTop: 16 }}>
            Easy one-click deposits, no impermanent loss risk, ideal for crypto newcomers.
          </Typography>
        </CardItem>
        <CardItem>
          <Img src={theme === 'dark' ? '/single-asset-vault3-dark.png' : '/single-asset-vault3.png'} />
          <Typography variant="label2" fontWeight="light" color="secondary" style={{ marginTop: 16 }}>
            Vaults diversify tokens across top strategies for higher returns and minimized risks.
          </Typography>
        </CardItem>
      </CardContainer>
      <ButtonContainer style={{ display: 'flex', justifyContent: 'center' }}>
        <SeeDocumentation>
          <a href="https://docs.kai.finance/single-asset-vaults" target="_blank" rel="noreferrer">
            See Documentation <IconLink />
          </a>
        </SeeDocumentation>
      </ButtonContainer>
    </Container>
  )
}
